import type { Root } from "../../core/Root";
import type { Bullet } from "../../core/render/Bullet";
import type { IDisposer } from "../../core/util/Disposer";

import { MapPointSeries, IMapPointSeriesSettings, IMapPointSeriesPrivate, IMapPointSeriesDataItem } from "./MapPointSeries";
import { Component, DataItem, IComponentDataItem } from "../../core/render/Component";
import { Container } from "../../core/render/Container";
import { Label } from "../../core/render/Label";

import * as $array from "../../core/util/Array";
import * as $object from "../../core/util/Object";
import * as d3hierarchy from "d3-hierarchy";
import * as $math from "../../core/util/Math";


export interface IClusteredDataItem extends IComponentDataItem {
	/**
	 * Data items of the points in the cluster.
	 */
	children?: Array<DataItem<IMapPointSeriesDataItem>>;

	/**
	 * The bullet that shows the cluster.
	 */
	bullet?: Bullet;

	/**
	 * Group id of the cluster's points.
	 */
	groupId?: string

	/**
	 * Longitude of the cluster, in degrees.
	 */
	longitude?: number;

	/**
	 * Latitude of the cluster, in degrees.
	 */
	latitude?: number;

	/**
	 * Average altitude of the cluster's data items, in metres.
	 *
	 * @since 5.21.0
	 */
	altitude?: number;
}

export interface IClusteredPointSeriesDataItem extends IMapPointSeriesDataItem {
	/**
	 * Id of the point's group: it clusters only with points of the same group.
	 */
	groupId?: string

	/**
	 * The cluster the point is in, if any.
	 * @readonly
	 */
	cluster?: DataItem<IClusteredDataItem>;

	/**
	 * How far, in pixels, scattering moved the bullet horizontally.
	 */
	dx?: number;

	/**
	 * How far, in pixels, scattering moved the bullet vertically.
	 */
	dy?: number;
}

export interface IClusteredPointSeriesPrivate extends IMapPointSeriesPrivate {

}

export interface IClusteredPointSeriesSettings extends IMapPointSeriesSettings {
	/**
	 * Field in `data` that holds the id of the point's group. Points cluster
	 * only with points of the same group, such as the same continent.
	 *
	 * @see {@link https://docs.amcharts.com/v5/charts/map-chart/clustered-point-series/#group-segregation} for more info
	 * @default "groupId"
	 */
	groupIdField?: string;

	/**
	 * Points closer than this many pixels to each other are clustered.
	 *
	 * @see {@link https://docs.amcharts.com/v5/charts/map-chart/clustered-point-series/#minimal-distance} for more info
	 * @default 20
	 */
	minDistance?: number;

	/**
	 * A function that returns the [[Bullet]] to show a cluster with. Without
	 * it, points aren't clustered.
	 *
	 * @see {@link https://docs.amcharts.com/v5/charts/map-chart/clustered-point-series/#group-bullet} for more info
	 */
	clusteredBullet?: (root: Root, series: ClusteredPointSeries, dataItem: DataItem<IClusteredDataItem>) => Bullet | undefined;

	/**
	 * Once clustering stops (see `stopClusterZoom`), points closer than this
	 * many pixels to each other are scattered so that all of them show.
	 *
	 * @see {@link https://docs.amcharts.com/v5/charts/map-chart/clustered-point-series/#scatter-settings} for more info
	 * @default 3
	 * @since 5.5.7
	 */
	scatterDistance?: number;

	/**
	 * Radius in pixels each bullet is taken to have when scattering them.
	 *
	 * @see {@link https://docs.amcharts.com/v5/charts/map-chart/clustered-point-series/#scatter-settings} for more info
	 * @default 8
	 * @since 5.5.7
	 */
	scatterRadius?: number;

	/**
	 * Clustering stops, and scattering starts, once the map is zoomed to
	 * `maxZoomLevel` times this.
	 *
	 * @see {@link https://docs.amcharts.com/v5/charts/map-chart/clustered-point-series/#scatter-settings} for more info
	 * @default 0.95
	 * @since 5.5.7
	 */
	stopClusterZoom?: number


	/**
	 * Waits this many milliseconds after the zoom level changes before
	 * clustering again. Helps with many data items.
	 *
	 * @default 0
	 * @since 5.9.11
	 */
	clusterDelay?: number;
}

/**
 * A [[MapPointSeries]] that groups points close together on screen into
 * clusters. Clusters are worked out again whenever the zoom level changes.
 *
 * @see {@link https://docs.amcharts.com/v5/charts/map-chart/clustered-point-series/} for more info
 * @since 5.5.6
 * @important
 */
export class ClusteredPointSeries extends MapPointSeries {

	public static className: string = "ClusteredPointSeries";
	public static classNames: Array<string> = MapPointSeries.classNames.concat([ClusteredPointSeries.className]);

	declare public _settings: IClusteredPointSeriesSettings;
	declare public _privateSettings: IClusteredPointSeriesPrivate;
	declare public _dataItemSettings: IClusteredPointSeriesDataItem;

	protected _dataItem: DataItem<this["_dataItemSettings"]> = this.makeDataItem({});
	protected _clusterIndex: number = 0;
	protected _clusters: Array<Array<DataItem<this["_dataItemSettings"]>>> = [];
	public clusteredDataItems: Array<DataItem<IClusteredDataItem>> = [];

	protected _scatterIndex: number = 0;
	protected _scatters: Array<Array<DataItem<this["_dataItemSettings"]>>> = [];

	public _packLayout = d3hierarchy.pack();

	protected _spiral: Array<{ x: number, y: number }> = [];

	protected _clusterDP?: IDisposer;

	protected _previousZL: number = 0;

	protected _afterNew() {
		this.fields.push("groupId");
		this._setRawDefault("groupIdField", "groupId");

		super._afterNew();
	}

	protected _afterDataChange(): void {
		super._afterDataChange();

		this._previousZL = 0;
	}

	public _updateChildren() {
		super._updateChildren();

		if (this.isDirty("scatterRadius")) {
			this._spiral = $math.spiralPoints(0, 0, 300, 300, 0, 3, 3, 0, 0)
		}

		const chart = this.chart;

		if (chart) {

			const zoomLevel = chart.get("zoomLevel", 1);

			if (zoomLevel != this._previousZL) {
				const clusterDelay = this.get("clusterDelay", 0);
				if (clusterDelay) {
					if (this._clusterDP) {
						this._clusterDP.dispose();

						this._clusterDP = this.setTimeout(() => {
							this._doTheCluster();
						}, clusterDelay)
					}
					else {
						// first time without delay
						this._doTheCluster();
						this._clusterDP = this.setTimeout(() => { }, 0);
					}
				}
				else {
					this._doTheCluster();
				}

				this._previousZL = zoomLevel;
			}

			$array.each(this.clusteredDataItems, (dataItem) => {
				const bullet = dataItem.get("bullet" as any);
				const longitude = dataItem.get("longitude", 0);
				const latitude = dataItem.get("latitude", 0);
				this._positionBulletReal(bullet, { type: "Point", coordinates: [longitude, latitude] }, [longitude, latitude], undefined, dataItem.get("altitude"));
			})
		}
	}


	protected _doTheCluster() {
		const groups: { [index: string]: Array<DataItem<IClusteredPointSeriesDataItem>> } = {};
		// distribute to groups
		$array.each(this.dataItems, (dataItem) => {
			const groupId = dataItem.get("groupId", "_default");

			if (!groups[groupId]) {
				groups[groupId] = [];
			}
			groups[groupId].push(dataItem);
		})

		this._scatterIndex = -1;
		this._scatters = [];
		this._clusterIndex = -1;
		this._clusters = [];

		$array.each(this.clusteredDataItems, (dataItem) => {
			dataItem.setRaw("children", undefined);
		})

		$array.each(this.dataItems, (dataItem) => {
			dataItem.setRaw("cluster", undefined);
		})

		$object.each(groups, (_key, group) => {
			this._scatterGroup(group);
		})


		$object.each(groups, (_key, group) => {
			this._clusterGroup(group);
		})

		$array.each(this.dataItems, (dataItem) => {
			if (!dataItem.get("cluster")) {
				const bullets = dataItem.bullets;
				if (bullets) {
					$array.each(bullets, (bullet) => {
						const sprite = bullet.get("sprite");
						if (sprite) {
							sprite.set("forceHidden", false);
						}
					})
				}
			}
		})
	}

	/**
	 * Zooms the map to fit the points of a cluster.
	 *
	 * @param  dataItem  Data item of the cluster
	 * @param  rotate    Also rotate the map to bring the cluster to the middle, as on a globe
	 * @see {@link https://docs.amcharts.com/v5/charts/map-chart/clustered-point-series/#drill-down} for more info
	 */
	public zoomToCluster(dataItem: DataItem<IClusteredDataItem>, rotate?: boolean) {
		this.zoomToDataItems(dataItem.get("children", []), rotate);
	}

	protected _clusterGroup(dataItems: Array<DataItem<IClusteredPointSeriesDataItem>>) {
		const chart = this.chart;
		if (chart && chart.get("zoomLevel", 1) >= chart.get("maxZoomLevel", 100) * this.get("stopClusterZoom", 0.95)) {
			// void
		}
		else {
			const minDistance = this.get("minDistance", 20);
			const minDistSq = minDistance * minDistance;

			// Pre-filter to candidates with valid points
			const candidates: Array<{ di: DataItem<IClusteredPointSeriesDataItem>, x: number, y: number }> = [];
			for (let i = 0; i < dataItems.length; i++) {
				const di = dataItems[i];
				if (!di.get("clipped")) {
					const pt = di.get("point");
					if (pt) {
						candidates.push({ di: di, x: pt.x, y: pt.y });
					}
				}
			}

			// Spatial grid: cell size = minDistance so neighbors are always in adjacent cells
			const grid = new Map<string, number[]>();
			for (let i = 0; i < candidates.length; i++) {
				const c = candidates[i];
				const key = Math.floor(c.x / minDistance) + "," + Math.floor(c.y / minDistance);
				let cell = grid.get(key);
				if (!cell) {
					cell = [];
					grid.set(key, cell);
				}
				cell.push(i);
			}

			const visited = new Uint8Array(candidates.length);

			for (let i = 0; i < candidates.length; i++) {
				if (visited[i]) continue;
				visited[i] = 1;

				this._clusterIndex++;
				this._clusters[this._clusterIndex] = [];
				const cluster = this._clusters[this._clusterIndex];

				const seed = candidates[i];
				cluster.push(seed.di);

				// BFS: only check 9 neighboring cells per expansion
				const queue = [seed];
				let head = 0;

				while (head < queue.length) {
					const pt = queue[head++];
					const cx = Math.floor(pt.x / minDistance);
					const cy = Math.floor(pt.y / minDistance);

					for (let dcx = -1; dcx <= 1; dcx++) {
						for (let dcy = -1; dcy <= 1; dcy++) {
							const cell = grid.get((cx + dcx) + "," + (cy + dcy));
							if (cell) {
								for (let k = 0; k < cell.length; k++) {
									const idx = cell[k];
									if (visited[idx]) continue;
									const c = candidates[idx];
									const dx = c.x - pt.x;
									const dy = c.y - pt.y;
									if (dx * dx + dy * dy < minDistSq) {
										visited[idx] = 1;
										cluster.push(c.di);
										queue.push(c);
									}
								}
							}
						}
					}
				}
			}
		}

		let i = 0;

		const bulletMethod = this.get("clusteredBullet");
		if (bulletMethod) {
			$array.each(this._clusters, (cluster) => {
				let sumX = 0;
				let sumY = 0;

				// For raised points: their average altitude and place on the earth
				let sumAltitude = 0;
				let earthX = 0;
				let earthY = 0;
				let earthZ = 0;

				let len = cluster.length;

				if (len > 1) {

					let clusteredDataItem = this.clusteredDataItems[i];
					if (!clusteredDataItem) {
						clusteredDataItem = new DataItem(this, undefined, {});

						const bullet = clusteredDataItem.set("bullet" as any, bulletMethod(this._root, this, clusteredDataItem));

						if (bullet) {
							const sprite = bullet.get("sprite");
							if (sprite) {
								this.bulletsContainer.children.push(sprite);
								sprite._setDataItem(clusteredDataItem);

								this.root.events.once("frameended", () => {
									if (sprite instanceof Container) {
										sprite.walkChildren((child) => {
											if (child instanceof Component) {
												child.markDirtyValues();
											}
										});
									}
								})
							}
						}

						this.clusteredDataItems.push(clusteredDataItem)
					}

					let groupId;

					$array.each(cluster, (dataItem) => {
						dataItem.setRaw("cluster", clusteredDataItem);

						const point = dataItem.get("point");
						if (point) {
							sumX += point.x;
							sumY += point.y;
						}

						const longitude = dataItem.get("longitude", 0) * $math.RADIANS;
						const latitude = dataItem.get("latitude", 0) * $math.RADIANS;
						sumAltitude += dataItem.get("altitude", 0);
						earthX += Math.cos(latitude) * Math.cos(longitude);
						earthY += Math.cos(latitude) * Math.sin(longitude);
						earthZ += Math.sin(latitude);

						const bullets = dataItem.bullets;
						if (bullets) {
							$array.each(bullets, (bullet) => {
								const sprite = bullet.get("sprite");
								if (sprite) {
									sprite.set("forceHidden", true);
								}
							})
						}
						groupId = dataItem.get("groupId");
					})

					let averageX = sumX / len;
					let averageY = sumY / len;

					clusteredDataItem.setRaw("children" as any, cluster);
					clusteredDataItem.setRaw("groupId", groupId);

					const prevLen = clusteredDataItem.get("value" as any);
					clusteredDataItem.setRaw("value" as any, len);

					const altitude = sumAltitude / len;
					clusteredDataItem.setRaw("altitude", altitude || undefined);

					const bullet = clusteredDataItem.get("bullet" as any);
					if (bullet) {

						// A raised point is not above the ground at its screen position
						let geoPoint = altitude ? {
							longitude: Math.atan2(earthY, earthX) * $math.DEGREES,
							latitude: Math.atan2(earthZ, Math.sqrt(earthX * earthX + earthY * earthY)) * $math.DEGREES
						} : this.chart!.invert({ x: averageX, y: averageY });
						if (geoPoint) {
							clusteredDataItem.setAll({
								longitude: geoPoint.longitude,
								latitude: geoPoint.latitude,
							});
						}

						this._positionBullets(clusteredDataItem)

						const sprite = bullet.get("sprite");
						if (sprite) {
							sprite.set("forceHidden", false);
							//sprite.setAll({ x: averageX, y: averageY });

							if (prevLen != len) {
								if (sprite instanceof Container) {
									sprite.walkChildren((child) => {
										if (child instanceof Label) {
											child.text.markDirtyText();
										}
									})
								}
							}
						}
					}
					i++;
				}
			})
		}

		$array.each(this.clusteredDataItems, (dataItem) => {
			let children = dataItem.get("children");
			if (!children || children.length == 0) {
				const bullet = dataItem.get("bullet" as any);
				if (bullet) {
					const sprite = bullet.get("sprite");
					if (sprite) {
						sprite.set("forceHidden", true);
					}
				}
			}
		})
	}

	protected _onDataClear() {
		super._onDataClear();

		$array.each(this.clusteredDataItems, (dataItem) => {
			const bullet = dataItem.get("bullet" as any);
			if (bullet) {
				const sprite = bullet.get("sprite");
				if (sprite) {
					sprite.dispose();
				}
			}
		})
		this.clusteredDataItems = [];

	}


	protected _scatterGroup(dataItems: Array<DataItem<IClusteredPointSeriesDataItem>>) {
		const chart = this.chart;
		if (chart && chart.get("zoomLevel", 1) >= chart.get("maxZoomLevel", 100) * this.get("stopClusterZoom", 0.95)) {
			const scatterDistance = this.get("scatterDistance", 5);
			const scatterDistSq = scatterDistance * scatterDistance;

			// Pre-filter to candidates with valid points
			const candidates: Array<{ di: DataItem<IClusteredPointSeriesDataItem>, x: number, y: number }> = [];
			for (let i = 0; i < dataItems.length; i++) {
				const di = dataItems[i];
				if (!di.get("clipped")) {
					const pt = di.get("point");
					if (pt) {
						candidates.push({ di: di, x: pt.x, y: pt.y });
					}
				}
			}

			// Spatial grid: cell size = scatterDistance
			const grid = new Map<string, number[]>();
			for (let i = 0; i < candidates.length; i++) {
				const c = candidates[i];
				const key = Math.floor(c.x / scatterDistance) + "," + Math.floor(c.y / scatterDistance);
				let cell = grid.get(key);
				if (!cell) {
					cell = [];
					grid.set(key, cell);
				}
				cell.push(i);
			}

			const visited = new Uint8Array(candidates.length);

			for (let i = 0; i < candidates.length; i++) {
				if (visited[i]) continue;
				visited[i] = 1;

				this._scatterIndex++;
				this._scatters[this._scatterIndex] = [];
				const scatter = this._scatters[this._scatterIndex];

				const seed = candidates[i];
				scatter.push(seed.di);

				const queue = [seed];
				let head = 0;

				while (head < queue.length) {
					const pt = queue[head++];
					const cx = Math.floor(pt.x / scatterDistance);
					const cy = Math.floor(pt.y / scatterDistance);

					for (let dcx = -1; dcx <= 1; dcx++) {
						for (let dcy = -1; dcy <= 1; dcy++) {
							const cell = grid.get((cx + dcx) + "," + (cy + dcy));
							if (cell) {
								for (let k = 0; k < cell.length; k++) {
									const idx = cell[k];
									if (visited[idx]) continue;
									const c = candidates[idx];
									const dx = c.x - pt.x;
									const dy = c.y - pt.y;
									if (dx * dx + dy * dy < scatterDistSq) {
										visited[idx] = 1;
										scatter.push(c.di);
										queue.push(c);
									}
								}
							}
						}
					}
				}
			}

			$array.each(this._scatters, (scatter) => {
				let len = scatter.length;

				if (len > 1) {
					let previousCircles: Array<{ x: number, y: number, radius: number }> = [];
					let s = 0;
					let radius = this.get("scatterRadius", 8);
					$array.each(scatter, (dataItem) => {
						let spiralPoint = this._spiral[s];
						let intersects = true;

						if (previousCircles.length > 0) {
							while (intersects) {
								$array.each(previousCircles, (previousCircle) => {
									intersects = false;
									while ($math.circlesOverlap({ x: spiralPoint.x, y: spiralPoint.y, radius: radius }, previousCircle)) {
										s++;

										if (this._spiral[s] == undefined) {
											intersects = false;
										}
										else {
											intersects = true;
											spiralPoint = this._spiral[s];
										}
									}
								})
							}
						}

						const dx = spiralPoint.x;
						const dy = spiralPoint.y;

						previousCircles.push({ x: dx, y: dy, radius: radius });

						dataItem.set("dx", dx);
						dataItem.set("dy", dy);

						const bullets = dataItem.bullets;
						if (bullets) {
							$array.each(bullets, (bullet) => {
								const sprite = bullet.get("sprite");
								if (sprite) {
									sprite.setAll({ dx: dx, dy: dy });
								}
							})
						}
					})
				}
			})
		}
	}

}
